import { useCallback, useMemo, useState } from 'react';
import { Nami, NamiLanguageCodes, NamiLogLevel } from '@namiml/web-sdk';
import ErrorMessage from '../../components/ErrorMessage';
import OptionSelector, { OptionSelectorOption } from '../../components/OptionSelector';
import EditableButton from '../../components/EditableButton';
import { dispatchSdkStateChange } from '../../hooks/useSdkInitialized';
import {
  NAMI_SDK_STATE_KEY,
  NFL_VIZIO_PLATFORM_ID,
  STAGING_APP_PLATFORM_ID,
  STAGING_APP_PLATFORM_ID_QA,
  NFL_WEB_PLATFORM_ID,
  NFL_WEB_PLATFORM_ID_PROD,
} from '../../utils/const';
import { namiCompressedInitialConfig } from '../../data/intialConfig/nami';
import { nflInitialConfig } from '../../data/intialConfig/nfl';
import Loader from '../../components/Loader';

const USE_STAGING_API = 'useStagingAPI';
const APP_US2 = 'appus2';

const CONFIG_OPTIONS = {
  languages: ['af', 'ar', 'bg', 'ca', 'cs', 'da', 'en'] as NamiLanguageCodes[],
  logLevels: ['debug', 'info', 'error', 'warn'] as NamiLogLevel[],
  namiHosts: {
    [USE_STAGING_API]: 'Staging',
    production: 'Production',
    [APP_US2]: 'Production app-us2',
    custom: 'Custom',
  },
  platformIds: {
    staging: {
      Platform_ID: STAGING_APP_PLATFORM_ID,
      Platform_ID_QA: STAGING_APP_PLATFORM_ID_QA,
    },
    nfl: {
      NFL_Web_Platform_ID: NFL_WEB_PLATFORM_ID,
      NFL_Web_Platform_ID_Prod: NFL_WEB_PLATFORM_ID_PROD,
      NFL_Vizio_Platform_ID: NFL_VIZIO_PLATFORM_ID,
    },
  },
  formFactors: {
    desktop: 'Desktop',
    phone: 'Phone',
    television: 'Television',
    tablet: 'Tablet',
  },
};

interface ConfigPanelProps {
  onConfigured: () => void;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({ onConfigured }) => {
  const [namiHost, setNamiHost] = useState<string>(USE_STAGING_API);
  const [namiCommandAPIHost, setNamiCommandAPIHost] = useState<string>('');
  const [appPlatformID, setAppPlatformID] = useState<string>('');
  const [sendInitialConfig, setInitialConfig] = useState<boolean>(false);
  const [language, setLanguage] = useState<NamiLanguageCodes>('en');
  const [logLevel, setLogLevel] = useState<NamiLogLevel>('debug');
  const [formFactor, setFormFactor] = useState<string>('desktop');
  const [disableCampaignUpdates, setDisableCampaignUpdates] = useState<boolean>(false);
  const [useSplitPosition, setUseSplitPosition] = useState<boolean>(false);
  const [splitPosition, setSplitPosition] = useState<string | undefined>('50');
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hostOptions = useMemo<OptionSelectorOption<string>[]>(
    () =>
      Object.entries(CONFIG_OPTIONS.namiHosts).map(([value, label]) => ({
        value,
        label,
      })),
    []
  );

  const languageOptions = useMemo<OptionSelectorOption<string>[]>(
    () => CONFIG_OPTIONS.languages.map((code) => ({ value: code, label: code.toUpperCase() })),
    []
  );

  const logLevelOptions = useMemo<OptionSelectorOption<string>[]>(
    () => CONFIG_OPTIONS.logLevels.map((level) => ({ value: level, label: level.toUpperCase() })),
    []
  );

  const formFactorOptions = useMemo<OptionSelectorOption<string>[]>(
    () => Object.entries(CONFIG_OPTIONS.formFactors).map(([value, label]) => ({ value, label })),
    []
  );

  const stagingPlatformButtons = useMemo(
    () => Object.entries(CONFIG_OPTIONS.platformIds.staging),
    []
  );
  const nflPlatformButtons = useMemo(
    () => Object.entries(CONFIG_OPTIONS.platformIds.nfl),
    []
  );

  const setupAPIHost = useCallback(
    (value: string) => {
      setNamiHost(value);
      setError('');

      if (value === APP_US2) {
        setNamiCommandAPIHost('https://app-us2.namiml.com/');
      } else if (value !== 'custom') {
        setNamiCommandAPIHost('');
      }

      if (value === USE_STAGING_API || value === APP_US2) {
        setAppPlatformID(appPlatformID);
      } else {
        setAppPlatformID('');
      }

      setInitialConfig(value === STAGING_APP_PLATFORM_ID || value === NFL_WEB_PLATFORM_ID);
    },
    [appPlatformID]
  );

  const setupAppPlatformID = useCallback(
    (selected: string) => {
      const next = appPlatformID === selected ? '' : selected;
      setAppPlatformID(next);
      setInitialConfig(next === STAGING_APP_PLATFORM_ID || next === NFL_WEB_PLATFORM_ID);
      if (next === STAGING_APP_PLATFORM_ID) {
        setNamiHost(USE_STAGING_API);
      }
    },
    [appPlatformID]
  );

  const handleSplitPositionInput = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        setSplitPosition(undefined);
        return;
      }
      const valid = /^\d{1,3}(?:\.\d+)?$/.test(trimmed);
      if (!valid) {
        return;
      }
      const num = Number(trimmed);
      if (Number.isNaN(num)) {
        return;
      }
      const clamped = Math.min(100, Math.max(0, num));
      setSplitPosition(String(clamped));
    },
    []
  );

  const toggleSplitPosition = useCallback(() => {
    setUseSplitPosition((prev) => {
      const next = !prev;
      if (next && (!splitPosition || splitPosition === '')) {
        setSplitPosition('50');
      }
      if (!next) {
        setSplitPosition(undefined);
      }
      return next;
    });
  }, [splitPosition]);

  const configureSDK = useCallback(async () => {
    setIsLoading(true);
    setMessage('');
    setError('');

    if (!appPlatformID) {
      setError('App Platform ID is required.');
      return;
    }

    try {
      const namiCommands = (() => {
        const commands: string[] = ['validateProductGroups'];
        if (['custom', APP_US2].includes(namiHost)) {
          if (!namiCommandAPIHost.trim()) {
            setError('Provide a command API host for the selected Nami host.');
            throw new Error('Command API host missing');
          }
          commands.push(`namiAPIHost=${namiCommandAPIHost.trim()}`);
        } else {
          commands.push(namiHost);
        }
        if (disableCampaignUpdates) {
          commands.push('disableCampaignUpdates');
        }
        if (useSplitPosition) {
          if (splitPosition !== undefined && splitPosition !== '') {
            const num = Number(splitPosition);
            if (!Number.isNaN(num)) {
              commands.push(`splitPosition=${num}`);
            }
          }
        }
        return commands;
      })();

      const state = await Nami.configure({
        appPlatformID,
        namiLanguageCode: language,
        logLevel,
        initialConfig: sendInitialConfig
          ? namiHost === APP_US2
            ? nflInitialConfig
            : namiCompressedInitialConfig
          : undefined,
        namiCommands,
        formFactor,
      });

      localStorage.setItem(NAMI_SDK_STATE_KEY, JSON.stringify(state));
      dispatchSdkStateChange();

      if (state.sdkInitialized) {
        setMessage('SDK configured successfully');
        onConfigured();
      } else {
        setMessage('SDK not configured');
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Command API host missing') {
        return;
      }
      const details = err instanceof Error ? err.message : String(err);
      setError(`Failed to configure SDK: ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    appPlatformID,
    disableCampaignUpdates,
    formFactor,
    language,
    logLevel,
    namiCommandAPIHost,
    namiHost,
    onConfigured,
    sendInitialConfig,
    splitPosition,
    useSplitPosition,
  ]);

  return (
    <div className="panel stack">
      {isLoading && <Loader />}
      <div>
        <h1 className="panel-title">Configure Nami SDK</h1>
        <p className="panel-subtitle">Match the Next.js sample configuration flow with remote-friendly controls.</p>
      </div>

      {message && <ErrorMessage message={message} variant="success" />}
      {error && <ErrorMessage message={error} />}

      <OptionSelector
        title="Nami Host"
        value={namiHost}
        options={hostOptions}
        onSelect={(value) => setupAPIHost(value as string)}
        layout="column"
      />

      {['custom', APP_US2].includes(namiHost) && (
        <EditableButton
          label="Command API Host"
          value={namiCommandAPIHost}
          placeholder="Set Nami command API host"
          onSubmit={setNamiCommandAPIHost}
        />
      )}

      {(namiHost === USE_STAGING_API || namiHost === APP_US2) && (
        <div className="panel">
          <div className="panel-title">App Platform ID Presets</div>
          <div className="panel-subtitle">
            Choose a preset to toggle quickly between environments.
          </div>
          <div className="option-grid" data-layout="column">
            {(namiHost === USE_STAGING_API ? stagingPlatformButtons : nflPlatformButtons).map(
              ([label, value]) => (
                <button
                  key={value}
                  type="button"
                  className={`tv-button${appPlatformID === value ? ' active' : ''}`}
                  onClick={() => setupAppPlatformID(value)}
                >
                  {label}
                </button>
              )
            )}
          </div>
        </div>
      )}

      <EditableButton
        label="App Platform ID"
        value={appPlatformID}
        placeholder="Enter App Platform ID"
        onSubmit={(value) => {
          setAppPlatformID(value.trim());
          setInitialConfig(false);
        }}
      />

      <div className="option-grid" data-layout="row">
        <button
          type="button"
          className={`tv-button${sendInitialConfig ? ' active' : ''}`}
          onClick={() => setInitialConfig((prev) => !prev)}
        >
          {sendInitialConfig ? 'Initial Config: On' : 'Initial Config: Off'}
        </button>
        <button
          type="button"
          className={`tv-button${disableCampaignUpdates ? ' active' : ''}`}
          onClick={() => setDisableCampaignUpdates((prev) => !prev)}
        >
          {disableCampaignUpdates ? 'Campaign Updates Disabled' : 'Campaign Updates Enabled'}
        </button>
        <button
          type="button"
          className={`tv-button${useSplitPosition ? ' active' : ''}`}
          onClick={toggleSplitPosition}
        >
          {useSplitPosition ? 'Split Position: On' : 'Split Position: Off'}
        </button>
      </div>

      {useSplitPosition && (
        <EditableButton
          label="Split Position (%)"
          value={splitPosition ?? ''}
          placeholder="0 - 100"
          onSubmit={handleSplitPositionInput}
        />
      )}

      <OptionSelector
        title="Language"
        value={language}
        options={languageOptions}
        onSelect={(value) => setLanguage(value as NamiLanguageCodes)}
        layout="row"
        pageSize={languageOptions.length}
      />

      <OptionSelector
        title="Log Level"
        value={logLevel}
        options={logLevelOptions}
        onSelect={(value) => setLogLevel(value as NamiLogLevel)}
      />

      <OptionSelector
        title="Form Factor"
        value={formFactor}
        options={formFactorOptions}
        onSelect={(value) => setFormFactor(value as string)}
      />

      <div className="inline-actions" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="tv-button" onClick={configureSDK} disabled={isLoading}>
          {isLoading ? 'Configuring SDK...' : 'Configure SDK'}
        </button>
      </div>
    </div>
  );
};

export default ConfigPanel;
